import React, { useState } from 'react';
import { User, ArrowRight, Calculator } from 'lucide-react';
import { useApp } from '../context/AppContext';

const WelcomeScreen: React.FC = () => {
  const { dispatch } = useApp();
  const [userName, setUserName] = useState('');
  const [userColor, setUserColor] = useState('#3b82f6');
  const [userAvatar, setUserAvatar] = useState('👤');

  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
  ];

  const avatars = ['👤', '👨', '👩', '🧑', '👨‍💻', '👩‍💻', '🎓', '👨‍🎓', '👩‍🎓', '🤓', '😎', '🧙‍♂️'];

  const handleGetStarted = () => {
    if (!userName.trim()) {
      alert('Please enter your name');
      return;
    }

    const userId = `user_${Date.now()}`;
    dispatch({
      type: 'SET_CURRENT_USER',
      payload: {
        id: userId,
        name: userName.trim(),
        color: userColor,
        avatar: userAvatar,
        isAnonymous: false,
      }
    });
  };

  const handleAnonymousStart = () => {
    dispatch({ type: 'CREATE_ANONYMOUS_USER' });
  };

  return (
    <div className="fixed inset-0 bg-dark-900 flex items-center justify-center z-50 p-4">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Calculator Icons */}
        <div className="absolute top-10 left-10 w-8 h-8 text-accent-500/20 animate-pulse">
          <Calculator className="w-full h-full" />
        </div>
        <div className="absolute top-20 right-20 w-6 h-6 text-accent-400/30 animate-bounce">
          <Calculator className="w-full h-full" />
        </div>
        <div className="absolute bottom-20 left-20 w-10 h-10 text-accent-600/25 animate-pulse">
          <Calculator className="w-full h-full" />
        </div>
        <div className="absolute bottom-10 right-10 w-7 h-7 text-accent-300/20 animate-bounce">
          <Calculator className="w-full h-full" />
        </div>
        
        {/* Mathematical Symbols */}
        <div className="absolute top-1/4 left-1/4 text-4xl text-accent-500/10 animate-pulse">π</div>
        <div className="absolute top-1/3 right-1/3 text-3xl text-accent-400/15 animate-bounce">∑</div>
        <div className="absolute bottom-1/3 left-1/3 text-5xl text-accent-600/10 animate-pulse">∞</div>
        <div className="absolute bottom-1/4 right-1/4 text-2xl text-accent-300/20 animate-bounce">√</div>
        
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-accent-500/5 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-accent-400/5 to-transparent rounded-full blur-3xl animate-bounce"></div>
      </div>
      
      {/* Main Content - Larger and more spacious */}
      <div className="relative z-10 bg-dark-800/95 backdrop-blur-sm rounded-3xl w-full max-w-2xl p-8 shadow-2xl border border-dark-700">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-pulse">
            <User className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-accent-300 bg-clip-text text-transparent">
            Welcome to DeCalculator
          </h1>
          <p className="text-dark-400 text-sm sm:text-base">Set up your profile to get started</p>
        </div>

        {/* Two-column layout for better space utilization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Profile Setup */}
          <div className="space-y-6">
            <div>
              <label className="text-white font-medium mb-3 block text-lg">Your Name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-dark-700 text-white rounded-xl px-4 py-4 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500 text-base"
              />
            </div>

            <div>
              <label className="text-white font-medium mb-4 block text-lg">Choose Your Avatar</label>
              <div className="grid grid-cols-4 gap-3">
                {avatars.map((avatar) => (
                  <button
                    key={avatar}
                    onClick={() => setUserAvatar(avatar)}
                    className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                      userAvatar === avatar
                        ? 'border-accent-500 bg-accent-900 scale-110 shadow-lg'
                        : 'border-dark-600 hover:border-dark-500 hover:scale-105'
                    }`}
                  >
                    <span className="text-xl">{avatar}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-4 block text-lg">Choose Your Color</label>
              <div className="grid grid-cols-5 gap-3">
                {colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setUserColor(color)}
                    className={`w-14 h-14 rounded-xl border-2 transition-all duration-200 ${
                      userColor === color
                        ? 'border-white scale-110 shadow-lg'
                        : 'border-dark-600 hover:border-dark-500 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Preview and Actions */}
          <div className="space-y-6">
            <div className="bg-dark-700/50 rounded-2xl p-6 border border-dark-600">
              <h3 className="text-white font-medium mb-4 text-lg">Preview</h3>
              <div className="flex flex-col items-center space-y-4">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                  style={{ backgroundColor: userColor }}
                >
                  <span className="text-3xl">{userAvatar}</span>
                </div>
                <div className="text-center">
                  <p className="text-white font-medium text-lg">{userName || 'Your Name'}</p>
                  <p className="text-dark-400">Ready to calculate!</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleGetStarted}
                className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-accent-600 to-accent-700 hover:from-accent-700 hover:to-accent-800 text-white rounded-xl py-4 font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 text-lg"
              >
                <span>Get Started</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleAnonymousStart}
                className="w-full flex items-center justify-center space-x-3 bg-dark-600 hover:bg-dark-700 text-white rounded-xl py-4 font-medium transition-all duration-200 hover:shadow-md text-lg"
              >
                <span>Continue as Anonymous</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-dark-500">
            You can change these settings later in the settings menu
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
